'use client';

import { motion } from 'framer-motion';
import { cn } from '@/app/utils';
import type { UseCaseIntroSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { EASE_OUT_CUBIC, SectionShell } from '../section-shared';

export function Alternating({ section }: { section: UseCaseIntroSectionData }) {
  return (
    <SectionShell>
      <div className="space-y-16 sm:space-y-10">
        {section.items?.map((item, index) => {
          const isReversed = index % 2 === 1;

          return (
            <motion.div
              key={item._key || item.title}
              className={cn(
                'flex items-center justify-center gap-[90px] sm:flex-col sm:items-start sm:gap-8',
                isReversed && 'flex-row-reverse sm:flex-col',
              )}
              initial={{ opacity: 0, x: isReversed ? 60 : -60 }}
              transition={{ duration: 0.6, ease: EASE_OUT_CUBIC }}
              viewport={{ once: true, margin: '-80px' }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className={cn('w-[380px] shrink-0 overflow-hidden rounded-2xl bg-system-white08 sm:w-full')}>
                <MediaAsset className="aspect-[4/3]" media={item.media} />
              </div>

              <div className="min-w-0 max-w-[597px] flex-1">
                <h2 className="font-semibold text-[28px] text-white leading-[1.2] sm:text-[24px]">{item.title}</h2>
                <PortableTextContent className="mt-3" value={item.description} />
                {item.button?.href && item.button?.label && (
                  <a
                    className="mt-6 inline-flex h-12 w-fit items-center justify-center rounded-full border-2 border-[#0086FF] bg-transparent px-8 font-medium text-[#0086FF] text-sm transition-colors hover:bg-[#0086FF]/10"
                    href={item.button.href}
                    rel={/^https?:\/\//.test(item.button.href) ? 'noreferrer' : undefined}
                    target={/^https?:\/\//.test(item.button.href) ? '_blank' : undefined}
                  >
                    {item.button.label}
                  </a>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </SectionShell>
  );
}
